/**
 * `Safe.isModuleEnabled(reader)` lookup — spec §8.0.
 *
 * Uses the standard read-only web3 provider (no shielded envelope needed —
 * `isModuleEnabled` is a pure view function that does not gate on `msg.sender`).
 *
 * Result is held in a module-level cache keyed by (chainId, safe, reader) so
 * the banner does not refetch on every render. Call `refetch()` after the
 * user submits an enableModule tx, on chain/safe switch the cache key
 * changes automatically.
 */
import { useCallback, useEffect, useState } from 'react'
import type { Address } from 'viem'
import { Contract } from 'ethers'
import useSafeAddress from '@/hooks/useSafeAddress'
import { useWeb3ReadOnly } from '@/hooks/wallets/web3'
import { logError, Errors } from '@/services/exceptions'
import { useSrc20Config } from './useSrc20Config'

const SAFE_IS_MODULE_ENABLED_ABI = ['function isModuleEnabled(address module) external view returns (bool)']

const moduleCache = new Map<string, { enabled: boolean; checkedAt: number }>()
const cacheKey = (chainId: number, safe: string, reader: string) =>
  `${chainId}:${safe.toLowerCase()}:${reader.toLowerCase()}`
const CACHE_TTL_MS = 30_000

export interface UseIsSrc20ModuleEnabledResult {
  enabled: boolean | undefined
  loading: boolean
  refetch: () => Promise<void>
}

export const useIsSrc20ModuleEnabled = (): UseIsSrc20ModuleEnabledResult => {
  const cfg = useSrc20Config()
  const safeAddress = useSafeAddress()
  const provider = useWeb3ReadOnly()
  const [enabled, setEnabled] = useState<boolean | undefined>(undefined)
  const [loading, setLoading] = useState(false)

  const doFetch = useCallback(
    async (force: boolean) => {
      if (!cfg || !safeAddress || !provider) {
        setEnabled(undefined)
        return
      }
      const key = cacheKey(cfg.chainId, safeAddress, cfg.readerAddress)
      const cached = moduleCache.get(key)
      const now = Date.now()
      if (!force && cached && now - cached.checkedAt < CACHE_TTL_MS) {
        setEnabled(cached.enabled)
        return
      }
      setLoading(true)
      try {
        const safe = new Contract(safeAddress, SAFE_IS_MODULE_ENABLED_ABI, provider)
        const res: boolean = await safe.isModuleEnabled(cfg.readerAddress)
        moduleCache.set(key, { enabled: res, checkedAt: now })
        setEnabled(res)
      } catch (err) {
        // A revert here typically means the Safe contract is older than 1.3 or
        // the address is not a Safe. Default to `false` so the banner offers
        // re-enable; the actual reader read will produce a clearer error.
        const msg = err instanceof Error ? err.message : String(err)
        logError(Errors._609, msg)
        moduleCache.set(key, { enabled: false, checkedAt: now })
        setEnabled(false)
      } finally {
        setLoading(false)
      }
    },
    [cfg, safeAddress, provider],
  )

  useEffect(() => {
    void doFetch(false)
  }, [doFetch])

  const refetch = useCallback(async () => {
    if (cfg && safeAddress) {
      moduleCache.delete(cacheKey(cfg.chainId, safeAddress, cfg.readerAddress))
    }
    await doFetch(true)
  }, [cfg, safeAddress, doFetch])

  return { enabled, loading, refetch }
}

export default useIsSrc20ModuleEnabled

/** Invalidate the cache entry for the given Safe so the next call refetches. */
export const invalidateModuleEnabledCache = (chainId: number, safe: Address, reader: Address): void => {
  moduleCache.delete(cacheKey(chainId, safe, reader))
}
